import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import DashboardLayout from '../components/DashboardLayout';
import { medicationService } from '../Services/medicationService';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Pill, Search, Send, Bot, User as UserIcon, Loader2, ChevronDown, ChevronUp, 
  AlertCircle, CheckCircle, XCircle, Info, BadgeCheck, Baby, Settings, ShieldAlert, Package
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import QuotaLimitModal from '../components/QuotaLimitModal';

const MedicationsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [medicationData, setMedicationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const { user, updateUser, hasPremiumAccess } = useAuth();

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [chatInput]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      toast.error('Veuillez entrer un nom de médicament');
      return;
    }

    setLoading(true);
    setMedicationData(null);
    setChatMessages([]);
    setShowDetails(false);

    try {
      const data = await medicationService.getMedicationInfo(searchQuery);

      if (!data.exists) {
        toast.error('Médicament non trouvé');
        return;
      }

      setMedicationData(data);
      toast.success('Informations chargées');

      // Quota non décompté pendant l'essai (aligné sur chatService.js côté serveur)
      if (!hasPremiumAccess) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const newCount = userDoc.data().daily_search_count || 0;
          updateUser({ daily_search_count: newCount });
        }
      }

      setChatMessages([
        {
          role: 'assistant',
          content: `Bonjour ! 💊 Je suis là pour répondre à vos questions sur **${data.name}**.`,
          timestamp: new Date(),
        },
      ]);
    } catch (error) {
      if (error.message === 'QUOTA_EXCEEDED') setShowQuotaModal(true);
      else toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  const handleChatSend = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading || !medicationData) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date() }]);
    setChatLoading(true);

    try {
      const aiResponse = await medicationService.askAboutMedication(medicationData.name, userMessage, chatMessages);
      setChatMessages(prev => [...prev, { role: 'assistant', content: aiResponse, timestamp: new Date() }]);
    } catch (error) {
      if (error.message === 'QUOTA_EXCEEDED') setShowQuotaModal(true);
      else toast.error("Erreur d'envoi");
    } finally {
      setChatLoading(false);
    }
  };

  const formatMessage = (content) => {
    const formatted = content
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-gray-900 dark:text-white">$1</strong>')
      .replace(/\n/g, '<br/>');
    return { __html: DOMPurify.sanitize(formatted) };
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        
        {/* --- HEADER & SEARCH --- */}
        <div className="text-center max-w-3xl mx-auto space-y-6">
          <div className="inline-flex p-3 bg-blue-100 dark:bg-blue-900/30 rounded-2xl">
            <Pill className="w-10 h-10 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 dark:text-white">
            Encyclopédie Médicale
          </h1>
          <p className="text-gray-500 text-lg">Informations officielles et chat interactif sur vos traitements.</p>

          <form onSubmit={handleSearch} className="relative group">
            <div className="absolute inset-y-0 left-5 flex items-center pointer-events-none">
              <Search className="w-6 h-6 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Entrez le nom du médicament (ex: Doliprane)..."
              className="w-full pl-14 pr-32 py-5 bg-white dark:bg-gray-800 border-none shadow-xl rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all text-lg outline-none text-gray-900 dark:text-white placeholder:text-gray-400"
              disabled={loading}
            />
            <div className="absolute right-2 top-2 bottom-2">
              <Button type="submit" disabled={loading || !searchQuery.trim()} className="h-full px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg">
                {loading ? <Loader2 className="animate-spin" /> : 'Rechercher'}
              </Button>
            </div>
          </form>
        </div>

        {medicationData && (
          <div className="grid lg:grid-cols-12 gap-8 items-start animate-in fade-in slide-in-from-bottom-4 duration-500">
            
            {/* --- LEFT COLUMN: INFO CARDS --- */}
            <div className="lg:col-span-7 space-y-6">
              
              {/* Main Badge */}
              <Card className="p-8 border-none bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-2xl overflow-hidden relative">
                <div className="relative z-10 flex justify-between items-start">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full w-fit backdrop-blur-md">
                      <BadgeCheck size={14} />
                      <span className="text-xs font-bold uppercase tracking-widest">Source Officielle BDPM</span>
                    </div>
                    <h2 className="text-4xl font-black uppercase">{medicationData.name}</h2>
                  </div>
                  <Pill size={60} className="opacity-20 -rotate-12" />
                </div>
              </Card>

              {/* Grid Layout for details */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="p-6 bg-white dark:bg-gray-800 border-none shadow-sm space-y-3">
                  <div className="flex items-center gap-2 text-blue-600 font-bold uppercase text-xs tracking-wider">
                    <Info size={16} /> Indications
                  </div>
                  <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">{medicationData.indications}</p>
                </Card>

                <Card className="p-6 bg-white dark:bg-gray-800 border-none shadow-sm space-y-3">
                  <div className="flex items-center gap-2 text-orange-500 font-bold uppercase text-xs tracking-wider">
                    <ShieldAlert size={16} /> Précautions
                  </div>
                  <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">{medicationData.interactions}</p>
                </Card>
              </div>

              {/* Posologie Section */}
              <Card className="p-6 border-none bg-white dark:bg-gray-800 shadow-sm">
                <div className="flex items-center gap-3 mb-6 border-b dark:border-gray-700 pb-4">
                  <Settings className="text-blue-500" />
                  <h3 className="font-bold text-xl text-gray-900 dark:text-white">Posologie & Administration</h3>
                </div>
                <div className="space-y-4">
                  {typeof medicationData.posologie === 'string' ? (
                    <p className="text-sm leading-relaxed whitespace-pre-line text-gray-700 dark:text-gray-300">{medicationData.posologie}</p>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-6">
                      {medicationData.posologie?.adulte && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
                          <span className="text-xs font-bold text-blue-600 uppercase block mb-1">Adulte</span>
                          <p className="text-sm text-gray-700 dark:text-blue-100">{medicationData.posologie.adulte}</p>
                        </div>
                      )}
                      {medicationData.posologie?.enfant && (
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-xl">
                          <span className="text-xs font-bold text-purple-600 uppercase block mb-1">Enfant</span>
                          <p className="text-sm text-gray-700 dark:text-purple-100">{medicationData.posologie.enfant}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>

              {/* Pregnancy Section */}
              <Card className="p-6 border-none bg-rose-50 dark:bg-rose-950/20 text-rose-900 dark:text-rose-200">
                <div className="flex items-center gap-3 mb-4">
                  <Baby className="w-6 h-6" />
                  <h3 className="font-bold text-lg">Grossesse & Allaitement</h3>
                </div>
                <div className="text-sm leading-relaxed italic">
                  {typeof medicationData.grossesse === 'string' ? medicationData.grossesse : (
                    <div className="space-y-2">
                      {medicationData.grossesse?.grossesse && <p><strong>Grossesse :</strong> {medicationData.grossesse.grossesse}</p>}
                      {medicationData.grossesse?.allaitement && <p><strong>Allaitement :</strong> {medicationData.grossesse.allaitement}</p>}
                    </div>
                  )}
                </div>
              </Card>

              {/* Secondary Actions */}
              <Button
                onClick={() => setShowDetails(!showDetails)}
                variant="ghost"
                className="w-full py-8 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all group"
              >
                <div className="flex flex-col items-center gap-1">
                  {showDetails ? <ChevronUp className="text-gray-700 dark:text-gray-300" /> : <ChevronDown className="text-gray-700 dark:text-gray-300" />}
                  <span className="font-bold text-sm uppercase tracking-widest text-gray-700 dark:text-gray-300">Composition & Présentation</span>
                </div>
              </Button>

              {showDetails && (
                <div className="animate-in slide-in-from-top-4 duration-300 grid md:grid-cols-2 gap-4">
                   <Card className="p-5 bg-white dark:bg-gray-900 text-sm">
                      <h4 className="font-bold mb-2 text-gray-900 dark:text-white flex items-center gap-2"><Package size={14} className="text-blue-500" /> Présentations</h4>
                      <p className="text-gray-500 dark:text-gray-400">{medicationData.presentations}</p>
                   </Card>
                   <Card className="p-5 bg-white dark:bg-gray-900 text-sm">
                      <h4 className="font-bold mb-2 text-gray-900 dark:text-white flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Composition</h4>
                      <p className="text-gray-500 dark:text-gray-400">{medicationData.composition}</p>
                   </Card>
                </div>
              )}
            </div>

            {/* --- RIGHT COLUMN: CONTEXTUAL CHAT --- */}
            <div className="lg:col-span-5 sticky top-6">
              <Card className="bg-white dark:bg-gray-900 border border-blue-200/60 dark:border-blue-700/40 shadow-2xl shadow-blue-500/10 h-[calc(100vh-24rem)] min-h-[600px] flex flex-col rounded-3xl overflow-hidden">
                <div className="p-6 bg-gradient-to-r from-blue-600 to-indigo-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg shadow-inner">
                      <Bot className="text-white w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-white">Assistant Pharma</h3>
                      <p className="text-xs text-blue-100/80">Expertise contextuelle sur {medicationData.name}</p>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/15 rounded-full backdrop-blur-sm">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                      </span>
                      <span className="text-xs text-white/90 font-medium">En ligne</span>
                    </div>
                  </div>
                </div>

                <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-hide">
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] p-4 rounded-2xl shadow-sm ${
                        msg.role === 'user' 
                        ? 'bg-blue-600 text-white rounded-tr-none' 
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-tl-none'
                      }`}>
                        <div 
                          className="text-sm prose dark:prose-invert prose-p:leading-relaxed" 
                          dangerouslySetInnerHTML={formatMessage(msg.content)} 
                        />
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex gap-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl w-fit">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce delay-75" />
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce delay-150" />
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="p-4 bg-white dark:bg-gray-900 border-t dark:border-gray-700">
                  <form onSubmit={handleChatSend} className="relative">
                    <textarea
                      ref={textareaRef}
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleChatSend(e);
                        }
                      }}
                      placeholder="Une question précise ?"
                      className="w-full pl-4 pr-12 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none resize-none text-sm transition-all text-gray-900 dark:text-white placeholder:text-gray-400"
                      rows="1"
                      disabled={chatLoading}
                    />
                    <Button 
                      type="submit" 
                      disabled={!chatInput.trim() || chatLoading}
                      className="absolute right-2 bottom-2 p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all"
                    >
                      <Send size={18} />
                    </Button>
                  </form>
                </div>
              </Card>
            </div>
          </div>
        )}

        {!medicationData && !loading && (
          <div className="py-20 flex flex-col items-center justify-center text-center space-y-4">
            <div className="relative">
               <div className="absolute inset-0 bg-blue-400 blur-3xl opacity-20 animate-pulse" />
               <Card className="relative p-12 bg-white dark:bg-gray-900 border-none shadow-2xl rounded-[40px] max-w-md">
                <Search size={48} className="mx-auto text-blue-500 mb-6" />
                <h3 className="text-2xl font-bold">Lancez votre recherche</h3>
                <p className="text-gray-500">Accédez instantanément à la base de données publique des médicaments et à un assistant dédié.</p>
               </Card>
            </div>
          </div>
        )}
      </div>

      {showQuotaModal && <QuotaLimitModal isOpen={showQuotaModal} onClose={() => setShowQuotaModal(false)} />}
    </DashboardLayout>
  );
};

export default MedicationsPage;